import React, { useState, useEffect } from 'react'; // Add useEffect import
import { 
  Box, 
  Typography, 
  TextField,
  InputAdornment,
  Paper,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AppsContent from './AppsContent';
import ToolsContent from './ToolsContent';
import { Application } from "@microsoft/microsoft-graph-types";
import InfoIcon from '@mui/icons-material/Info';
import DocumentationContent from './DocumentationContent';
import MenuBookIcon from '@mui/icons-material/MenuBook';
// Removed direct tool import; ToolsContent renders tools internally with tokens
// import ClaimsMappingPolicyTool from './ClaimsMappingPolicyTool';
// import BusinessIcon from '@mui/icons-material/Business';
import WelcomeDashboard from './WelcomeDashboard';

interface ContentAreaProps {
  activeSection: 'apps' | 'tools' | 'docs' | null;
  isMsalInitialized: boolean;
  userDisplayName: string | null;
  onAppSelect: (app: Application) => void;
  onSectionChange: (section: 'apps' | 'tools' | 'docs' | null) => void;
}

const ContentArea: React.FC<ContentAreaProps> = ({
  activeSection,
  isMsalInitialized,
  userDisplayName,
  onAppSelect,
  onSectionChange 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isToolActive, setIsToolActive] = useState(false); 

  // FIXED: Reset tool state when switching sections
  useEffect(() => {
    if (activeSection !== 'tools') {
      // When leaving tools section, reset tool-related state
      setIsToolActive(false);
    }
  }, [activeSection]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Tool selection and back are managed inside ToolsContent

  const handleToolStateChange = (toolActive: boolean) => {
    setIsToolActive(toolActive);
  };

  // FIXED: More precise logic for showing search
  const showSearch = (() => {
    // Always show search for apps
    if (activeSection === 'apps') {
      return true;
    }
    
    // For tools section, only hide search when a tool is actually active
    if (activeSection === 'tools') {
      return !isToolActive;
    }
    
    // For docs or no section, don't show search
    return false;
  })();

const renderContent = () => {
  if (!activeSection) {
    return (
      <WelcomeDashboard
        userDisplayName={userDisplayName}
        onOpenApps={() => onSectionChange('apps')}
        onOpenDocs={() => onSectionChange('docs')}
      />
    );
  }

    // Tools are rendered within ToolsContent; no direct rendering here

    switch (activeSection) {
      case 'apps':
        return (
          <AppsContent 
            searchQuery={searchQuery}
            isMsalInitialized={isMsalInitialized}
            userDisplayName={userDisplayName}
            onAppSelect={onAppSelect}
          />
        );
      case 'tools':
        return <ToolsContent 
          searchQuery={searchQuery} 
          onToolStateChange={handleToolStateChange}
        />;
      case 'docs':
  return <DocumentationContent onBack={() => { /* no-op from ContentArea */ }} />;
        
      default:
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: 'calc(100% - 60px)',
              p: 3
            }}
          >
            <Typography variant="h4" color="text.secondary" sx={{ mb: 2 }}>
              Welcome to AppConfig²
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 500 }}>
              Select a section from the sidebar to view applications, tools, or actions.
            </Typography>
          </Box>
        );
    }
  };

  const getSectionTitle = () => {
    switch (activeSection) {
      case 'apps':
        return 'Applications';
      case 'tools':
        return 'Tools';
      case 'docs':
        return 'Documentation';
      default:
        return 'Home';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: '#f3f3f3',
      }}
    >
      {/* Header with search */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderBottom: '1px solid rgba(0,0,0,0.09)',
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            mb: showSearch ? 2 : 0, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {getSectionTitle()}
          {activeSection === 'apps' && (
            <Tooltip title="Search by app name, appId, tenant type, or redirect URIs" placement="top">
              <InfoIcon 
                fontSize="small" 
                sx={{ 
                  ml: 1, 
                  color: 'text.secondary', 
                  cursor: 'help',
                  opacity: 0.7,
                  '&:hover': {
                    opacity: 1
                  }
                }} 
              />
            </Tooltip>
          )}
        </Typography>
        
        {showSearch && (
          <TextField
            placeholder={`Search ${activeSection || 'content'}...`}
            variant="outlined"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              backgroundColor: '#f5f5f5',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0,0,0,0.12)',
                },
              },
            }}
          />
        )}
      </Paper>

      {/* Content area */}
      <Box sx={{ p: 2, overflow: 'auto', flexGrow: 1 }}>
        {renderContent()}
      </Box>
    </Box>
  );
};

export default ContentArea;